<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-aria/i18n';
import {ClassAPI, HeaderInfo, TypeContext, FunctionAPI, TypeLink} from '@react-spectrum/docs';
import packageData from '../package.json';

```tsx import
import {useMessageFormatter} from '@react-aria/i18n';
```

---
category: Internationalization
---

# useMessageFormatter

<p>{docs.exports.useMessageFormatter.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['useMessageFormatter']} />

## Introduction

`useMessageFormatter` wraps the [intl-messageformat](https://formatjs.io/docs/intl-messageformat) library to provide
a React Hook that integrates with the i18n system in React Aria. It handles formatting strings for the current locale,
updating when the locale changes, and caching of message formatters for performance. See the 
[intl-messageformat](https://formatjs.io/docs/intl-messageformat) docs for information on how to create format strings.

## API

<FunctionAPI function={docs.exports.useMessageFormatter} links={docs.links} />

## Example

This example shows a simple component that displays a greeting, supporting both English and French. An object containing
the messages for each language is passed to `useMessageFormatter`, which returns a `formatMessage` function that can be
used to format the greeting. Two instances of the greeting component are rendered, using the [I18nProvider](I18nProvider.html)
to specify the language to display.

```tsx example
import {I18nProvider} from '@react-aria/i18n';

let messages = {
  'en-US': {
    'greeting': 'Hello, {name}!'
  },
  'fr-FR': {
    'greeting': "Bonjour, {name}!"
  }
};

function Greeting({name}) {
  let formatMessage = useMessageFormatter(messages);

  return (
    <p>{formatMessage('greeting', {name})}</p>
  );
}

<>
  <I18nProvider locale="en-US">
    <Greeting name="Devon" />
  </I18nProvider>

  <I18nProvider locale="fr-FR">
    <Greeting name="Devon" />
  </I18nProvider>
</>
```
